import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CandidateCard } from "./components/CandidateCard";
import { CandidateDetail } from "./components/CandidateDetail";
import { OpsCenter } from "./components/OpsCenter";
import { fetchRound, pendingDecisions, pruneQueue, queueDecision, submitRound } from "./api";
import type { Choice, Round } from "./types";

type Status = "loading" | "ready" | "empty" | "error" | "submitted";

export default function App() {
  const [round, setRound] = useState<Round | null>(null);
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string>("");
  const [online, setOnline] = useState(navigator.onLine);
  const [sending, setSending] = useState(false);
  // Hash routing rather than pushState: no server rewrite rule needed, and the
  // browser back button works for free on a phone.
  const [route, setRoute] = useState(() => location.hash);
  const abort = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    abort.current?.abort();
    const ac = new AbortController();
    abort.current = ac;
    setStatus("loading");
    try {
      const r = await fetchRound(ac.signal);
      if (ac.signal.aborted) return;
      if (!r) {
        setStatus("empty");
        return;
      }
      setRound(r);
      // Stale taps from an earlier round must not leak into this one.
      pruneQueue(r.candidates.map((c) => c.id));
      // Server decisions first, then anything queued offline on this device -
      // the local tap is newer than whatever the server last saw.
      setChoices({ ...r.decisions, ...pendingDecisions() });
      setStatus(r.status === "submitted" ? "submitted" : "ready");
    } catch (e) {
      if (ac.signal.aborted) return;
      setError(e instanceof Error ? e.message : String(e));
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void load();
    const on = () => {
      setOnline(true);
      void load();
    };
    const off = () => setOnline(false);
    const hash = () => setRoute(location.hash);
    // An installed PWA is resumed, not reloaded. Without this it renders
    // whatever it fetched days ago and looks broken - which is exactly how a
    // closed round keeps showing after a new one was published.
    const vis = () => {
      if (document.visibilityState === "visible") void load();
    };
    addEventListener("online", on);
    addEventListener("offline", off);
    addEventListener("hashchange", hash);
    document.addEventListener("visibilitychange", vis);
    return () => {
      removeEventListener("online", on);
      removeEventListener("offline", off);
      removeEventListener("hashchange", hash);
      document.removeEventListener("visibilitychange", vis);
      abort.current?.abort();
    };
  }, [load]);

  const choose = useCallback((id: string, choice: Choice) => {
    setChoices((prev) => {
      // Tapping the active choice clears it, so a mis-tap is undoable.
      const next = { ...prev };
      if (next[id] === choice) delete next[id];
      else next[id] = choice;
      return next;
    });
    queueDecision(id, choice);
    navigator.vibrate?.(8);
  }, []);

  const openDetail = useCallback((id: string) => {
    location.hash = `#/c/${id}`;
  }, []);

  const closeDetail = useCallback(() => {
    if (location.hash) history.back();
  }, []);

  const tally = useMemo(() => {
    const t = { select: 0, skip: 0, defer: 0 };
    for (const v of Object.values(choices)) t[v] += 1;
    return t;
  }, [choices]);

  const decided = Object.keys(choices).length;
  const total = round?.candidates.length ?? 0;
  const complete = total > 0 && decided === total;

  async function onSubmit() {
    if (!round) return;
    setSending(true);
    try {
      await submitRound(round.id, choices);
      setStatus("submitted");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStatus("error");
    } finally {
      setSending(false);
    }
  }

  // Ops is reachable in every state - including "no open round", which is
  // exactly when you most want to see what the council has been doing.
  if (route.startsWith("#/ops")) {
    return <OpsCenter onBack={() => (location.hash ? history.back() : undefined)} />;
  }

  if (status === "loading") {
    return (
      <div className="state">
        <div className="spinner" />
        <p>Loading round…</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="state">
        <h2>Could not reach the council</h2>
        <p>{error}</p>
        <p>
          <button className="submit" onClick={() => void load()}>
            Retry
          </button>
        </p>
      </div>
    );
  }

  if (status === "empty") {
    return (
      <div className="state">
        <h2>No open round</h2>
        <p>The council has not published candidates yet.</p>
        <p className="muted">It refreshes on its own whenever you reopen the app.</p>
        <p className="state-actions">
          <button className="submit" onClick={() => void load()}>
            Refresh
          </button>
          <button className="submit ghost" onClick={() => (location.hash = "#/ops")}>
            Ops centre
          </button>
        </p>
      </div>
    );
  }

  if (status === "submitted") {
    return (
      <div className="state">
        <h2>Round submitted</h2>
        <p>
          <b>{tally.select}</b> selected · {tally.skip} skipped · {tally.defer} deferred
        </p>
        <p>The council picks these up on its next run. Nothing executes from here.</p>
        <p className="muted">This screen clears when a new round is published.</p>
        <p className="state-actions">
          <button className="submit" onClick={() => void load()}>
            Check for a new round
          </button>
          <button className="submit ghost" onClick={() => (location.hash = "#/ops")}>
            Ops centre
          </button>
        </p>
      </div>
    );
  }

  if (!round) return null;

  const detailId = route.startsWith("#/c/") ? decodeURIComponent(route.slice(4)) : null;
  const detail = detailId ? round.candidates.find((c) => c.id === detailId) : undefined;
  if (detail) {
    return (
      <CandidateDetail
        candidate={detail}
        screen={round.screens[detail.screen]}
        choice={choices[detail.id]}
        onChoose={choose}
        onBack={closeDetail}
        disabled={sending}
      />
    );
  }

  // A screen that could only see a handful of names is a look, not a screen.
  const thin = round.screened > 0 && round.screened < 10;

  return (
    <div className="app">
      <header className="header">
        <div className="header-row">
          <h1>Round {round.id}</h1>
          <button className="ops-link" onClick={() => void load()} aria-label="Refresh round">
            ↻
          </button>
          <button className="ops-link" onClick={() => (location.hash = "#/ops")}>
            Ops ↗
          </button>
        </div>
        <div className="sub">
          {new Date(round.createdAt).toLocaleString()} · {decided}/{total} decided
        </div>
      </header>

      <main className="main">
        {!online && (
          <div className="offline">
            Offline — your taps are saved on this device and sent when you reconnect.
          </div>
        )}

        <div className={`coverage${thin ? " thin" : ""}`}>
          <span>{thin ? "⚠" : "◍"}</span>
          <span>
            Screened <b>{round.screened}</b> of <b>{round.universe}</b> names in the
            universe.
            {thin && " Too few to rank meaningfully — treat these as a look, not a screen."}
            {round.note && ` ${round.note}`}
          </span>
        </div>

        {round.candidates.map((c) => (
          <CandidateCard
            key={c.id}
            candidate={c}
            choice={choices[c.id]}
            onChoose={choose}
            onOpen={openDetail}
            disabled={sending}
          />
        ))}
      </main>

      <div className="bar">
        <div className="bar-inner">
          <span className="tally">
            <b>{tally.select}</b> select · {tally.skip} skip · {tally.defer} defer
          </span>
          <button
            className="submit"
            disabled={!complete || sending}
            onClick={() => void onSubmit()}
          >
            {sending ? "Sending…" : complete ? "Submit round" : `${total - decided} left`}
          </button>
        </div>
      </div>
    </div>
  );
}
